/**
 * Client-side NCID helpers — format matches BE:
 * NCID-{YY}-{CA|RN|PT}-{#####}
 * Legacy NPID-{YY}-… codes remain valid for lookup.
 * Permanent codes are issued only by the backend after Aadhaar verification.
 */

export const NCID_IDENTITY_LABEL = "NEMA CAREHUB IDENTITY (Lifetime)";
export const NCID_CODE_RE = /^(?:NCID|NPID)-(\d{2})-(CA|RN|PT)-(\d{5})$/;

/** ISO/IEC 7810 ID-1 portrait (standard vertical ID card). */
export const NCID_CARD_WIDTH_MM = 53.98;
export const NCID_CARD_HEIGHT_MM = 85.6;
export const NCID_CARD_ASPECT_RATIO = `${NCID_CARD_WIDTH_MM} / ${NCID_CARD_HEIGHT_MM}`;

export const STAFF_CATEGORY_NAMES = {
  CA: "Care Attendant",
  RN: "Nurse",
  PT: "Physiotherapist",
};

/** Map marketplace / form aliases → attendant | nurse | physiotherapist */
export function normalizeServiceRole(categoryOrRole) {
  const raw = String(categoryOrRole || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (!raw) return null;
  if (raw === "ca" || raw === "attendant" || raw === "care attendant" || raw === "caregiver") {
    return "attendant";
  }
  if (raw === "rn" || raw === "nurse" || raw === "nurses" || raw === "nursing" || /\bnurs/.test(raw)) {
    return "nurse";
  }
  if (
    raw === "pt" ||
    raw === "physio" ||
    raw === "physiotherapist" ||
    raw === "physiotherapy" ||
    /\bphysio/.test(raw)
  ) {
    return "physiotherapist";
  }
  return null;
}

/** Marketplace filter IDs used in CARE_TYPES selects */
export function toMarketplaceServiceRoleId(categoryOrRole) {
  const role = normalizeServiceRole(categoryOrRole) || "attendant";
  if (role === "nurse") return "nurses";
  if (role === "physiotherapist") return "physio";
  return "attendant";
}

export function resolveStaffCategoryCode(categoryOrRole) {
  const raw = String(categoryOrRole || "").trim().toLowerCase();
  if (!raw) return "CA";
  if (raw === "ca" || raw === "rn" || raw === "pt") return raw.toUpperCase();
  const role = normalizeServiceRole(raw);
  if (role === "nurse") return "RN";
  if (role === "physiotherapist") return "PT";
  return "CA";
}

export function resolveNcidCode(ncid) {
  if (ncid == null || ncid === "") return "";
  if (typeof ncid === "string") return ncid.trim();
  if (typeof ncid === "object") {
    return String(ncid.code || ncid.ncid || ncid.npid || ncid.value || "").trim();
  }
  return String(ncid).trim();
}

export function resolveNpidCode(ncid) {
  return resolveNcidCode(ncid);
}

/** User-facing label — legacy stored codes may still use an NPID- prefix. */
export function formatNcidForDisplay(code) {
  const raw = resolveNcidCode(code);
  if (!raw) return "";
  return raw.replace(/^NPID/i, "NCID");
}

export function staffCategoryName(codeOrRole) {
  const code = resolveStaffCategoryCode(codeOrRole);
  return STAFF_CATEGORY_NAMES[code] || "Care Attendant";
}

export function formatStaffCategoryLabel(codeOrRole) {
  const code = resolveStaffCategoryCode(codeOrRole);
  return `${code} · ${staffCategoryName(code)}`;
}

export function parseNcidParts(code) {
  const m = String(code || "")
    .trim()
    .toUpperCase()
    .match(NCID_CODE_RE);
  if (!m) return null;
  return {
    year: m[1],
    categoryCode: m[2],
    categoryName: staffCategoryName(m[2]),
    sequence: m[3],
  };
}

export function parseNpidParts(code) {
  return parseNcidParts(code);
}

/**
 * @deprecated Do not mint client-side NCIDs. Kept for parse/tests only — production
 * codes are issued by the backend at Aadhaar verification.
 */
export function buildNcidCodeFromId(caregiverId, createdAt, categoryOrRole) {
  const id = Number(caregiverId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const year = new Date(createdAt || Date.now()).getFullYear().toString().slice(-2);
  const cat = resolveStaffCategoryCode(categoryOrRole);
  return `NCID-${year}-${cat}-${String(id).padStart(5, "0")}`;
}

export function buildNpidCodeFromId(caregiverId, createdAt, categoryOrRole) {
  return buildNcidCodeFromId(caregiverId, createdAt, categoryOrRole);
}

function isLocalHostUrl(url) {
  try {
    const host = new URL(url).hostname;
    return host === "localhost" || host === "127.0.0.1";
  } catch {
    return /localhost|127\.0\.0\.1/i.test(String(url || ""));
  }
}

export function buildNcidVerifyUrl(caregiverId, code) {
  const id = Number(caregiverId);
  const ncid = String(code || "").trim();
  if (!Number.isFinite(id) || id <= 0 || !ncid) return null;
  if (!parseNcidParts(ncid)) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (!origin) return null;
  return `${origin}/caregivers/${id}?verify=${encodeURIComponent(ncid)}`;
}

export function buildNpidVerifyUrl(caregiverId, code) {
  return buildNcidVerifyUrl(caregiverId, code);
}

/**
 * Prefer a live-origin verify URL when the API baked localhost into QR links
 * (common when PUBLIC_WEB_ORIGIN was unset during local issue, then app is opened on deploy).
 */
export function resolveNcidVerifyUrl(caregiverId, code, existingVerifyUrl) {
  const live = buildNcidVerifyUrl(caregiverId, code);
  const existing = String(existingVerifyUrl || "").trim();
  if (!existing) return live;
  if (!live) return existing;
  const pageIsLocal =
    typeof window !== "undefined" && isLocalHostUrl(window.location.origin);
  if (isLocalHostUrl(existing) && !pageIsLocal) return live;
  return existing;
}

export function resolveNpidVerifyUrl(caregiverId, code, existingVerifyUrl) {
  return resolveNcidVerifyUrl(caregiverId, code, existingVerifyUrl);
}

export function ensureNcidProfile(caregiver, caregiverId) {
  if (!caregiver || typeof caregiver !== "object") return caregiver;
  const id = caregiver.id ?? caregiverId;
  const existing =
    (caregiver.ncid && typeof caregiver.ncid === "object" && caregiver.ncid) ||
    (caregiver.npid && typeof caregiver.npid === "object" ? caregiver.npid : {});
  const rawCode =
    existing.code ||
    (typeof caregiver.ncid === "string" ? caregiver.ncid : null) ||
    (typeof caregiver.npid === "string" ? caregiver.npid : null) ||
    null;
  // Only accept permanently issued codes that match NCID-{YY}-{CA|RN|PT}-{#####}.
  const code = parseNcidParts(rawCode) ? String(rawCode).trim().toUpperCase() : null;
  const parsed = code ? parseNcidParts(code) : null;

  const staffCategory =
    parsed?.categoryCode ||
    existing.staffCategory ||
    resolveStaffCategoryCode(
      caregiver.serviceRole ||
        caregiver.serviceType ||
        caregiver.category ||
        caregiver.profileExtended?.marketplacePrefs?.serviceRole
    );

  if (!code) {
    const pendingProfile = {
      label: existing.label || NCID_IDENTITY_LABEL,
      trustScore: existing.trustScore ?? null,
      trustLabel: existing.trustLabel ?? null,
      showNewBadge: existing.showNewBadge ?? false,
      staffCategory,
      levelBadge: existing.levelBadge ?? null,
      levelLabel: existing.levelLabel ?? null,
      registeredAt: existing.registeredAt ?? caregiver.registeredAt ?? null,
      lastUpdatedAt: existing.lastUpdatedAt ?? caregiver.updatedAt ?? null,
      issued: false,
      issuedAt: existing.issuedAt ?? null,
      activation: existing.activation || null,
      qrSvg: null,
      qrSvgLarge: null,
      code: null,
      verifyUrl: null,
    };
    return {
      ...caregiver,
      ncid: pendingProfile,
      npid: pendingProfile,
    };
  }

  const verifyUrl = resolveNcidVerifyUrl(id, code, existing.verifyUrl);
  const qrSvg =
    existing.verifyUrl &&
    verifyUrl &&
    existing.verifyUrl !== verifyUrl &&
    isLocalHostUrl(existing.verifyUrl)
      ? null
      : existing.qrSvg ?? null;
  const qrSvgLarge =
    existing.verifyUrl &&
    verifyUrl &&
    existing.verifyUrl !== verifyUrl &&
    isLocalHostUrl(existing.verifyUrl)
      ? null
      : existing.qrSvgLarge ?? null;

  const ncidProfile = {
    label: existing.label || NCID_IDENTITY_LABEL,
    trustScore: existing.trustScore ?? null,
    trustLabel: existing.trustLabel ?? null,
    showNewBadge: existing.showNewBadge ?? false,
    staffCategory,
    levelBadge: existing.levelBadge ?? null,
    levelLabel: existing.levelLabel ?? null,
    registeredAt: existing.registeredAt ?? caregiver.registeredAt ?? null,
    lastUpdatedAt: existing.lastUpdatedAt ?? caregiver.updatedAt ?? null,
    issued: existing.issued ?? true,
    issuedAt: existing.issuedAt ?? null,
    activation: existing.activation || null,
    qrSvg,
    qrSvgLarge,
    code: formatNcidForDisplay(code),
    verifyUrl,
  };

  return {
    ...caregiver,
    ncid: ncidProfile,
    npid: ncidProfile,
  };
}

export function ensureNpidProfile(caregiver, caregiverId) {
  return ensureNcidProfile(caregiver, caregiverId);
}

/** QR image URL fallback when backend SVG is not available yet. */
export function buildNcidQrImageUrl(verifyUrl, size = 56) {
  if (!verifyUrl) return null;
  const dim = Math.max(48, Math.min(256, Number(size) || 56));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${dim}x${dim}&margin=0&data=${encodeURIComponent(verifyUrl)}`;
}

export function buildNpidQrImageUrl(verifyUrl, size = 56) {
  return buildNcidQrImageUrl(verifyUrl, size);
}
